import { FileDownloadOutlined, OpenInNew, PhonelinkOffOutlined, ShareOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { FunctionComponent, JSX, useMemo, useState } from 'react'

import { useUtils } from '../../../../base/hooks/useUtils.tsx'
import { useImpresionDocumento } from '../hooks/useImpresionDocumento.ts'
import { MENSAJE_ISI_PRINT_NO_SOPORTADO } from '../services/dispositivo.ts'
import { getImpresionDocumentoLabel } from '../services/impresionDocumento.ts'
import {
  compartirPdf,
  descargarPdf,
  esUrlPublica,
  obtenerUrlPublicaPdf,
  puedeCompartirArchivos,
} from '../services/impresionPdf.ts'
import type { ImpresionDocumentoDialogProps } from './ImpresionDocumentoDialog'

/**
 * Opciones de un documento en teléfonos y tabletas, donde no hay isi-print: ver el PDF, compartirlo
 * (hoja del sistema: WhatsApp, correo, AirPrint o apps de impresión de Android), descargarlo y las
 * acciones extra del proyecto (ej. enviar por WhatsApp con Waapi). Reemplaza al diálogo de impresión.
 */
const ImpresionDocumentoMovil: FunctionComponent<ImpresionDocumentoDialogProps> = ({
  open,
  onClose,
  titulo: tituloProp,
  documento,
  pdf,
  obtenerPdf,
  filename,
  destinatario,
  whatsapp = false,
}) => {
  const impresion = useImpresionDocumento()
  const { toast } = useUtils()
  const titulo = tituloProp || (documento ? getImpresionDocumentoLabel(documento) : '') || 'Documento'

  /**
   * Abre el PDF en otra pestaña. La URL de un Blob se crea en el mismo clic (sin estado ni efecto) y se
   * libera un minuto después, cuando la pestaña ya lo cargó. Se abre de forma síncrona en el clic para
   * que Safari en iOS no lo bloquee como ventana emergente.
   */
  const ver = () => {
    if (!pdf) return
    if (typeof pdf === 'string') {
      window.open(pdf, '_blank', 'noopener,noreferrer')
      return
    }
    const url = URL.createObjectURL(pdf)
    window.open(url, '_blank', 'noopener,noreferrer')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  const compartible = useMemo(
    () =>
      puedeCompartirArchivos() ||
      (typeof navigator !== 'undefined' && !!navigator.share && esUrlPublica(pdf)),
    [pdf],
  )
  const [accion, setAccion] = useState<'compartir' | 'descargar' | null>(null)

  const compartir = async () => {
    if (!pdf) return
    setAccion('compartir')
    try {
      await compartirPdf(pdf, titulo, filename)
    } finally {
      setAccion(null)
    }
  }

  const descargar = async () => {
    if (!pdf) return
    setAccion('descargar')
    try {
      await descargarPdf(pdf, filename || titulo)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo descargar el documento')
    } finally {
      setAccion(null)
    }
  }

  const icono = (a: typeof accion, nodo: JSX.Element) =>
    accion === a ? <CircularProgress size={20} /> : nodo

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '85vh',
            pb: 'max(12px, env(safe-area-inset-bottom))',
          },
        },
      }}
    >
      <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mt: 1.25, mb: 1 }} />
      <Box sx={{ px: 2, pb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
          {titulo}
        </Typography>
      </Box>

      <List dense disablePadding>
        <ListItemButton disabled={!pdf} onClick={ver}>
          <ListItemIcon>
            <OpenInNew />
          </ListItemIcon>
          <ListItemText primary="Ver documento" secondary="Abre el PDF en el navegador" />
        </ListItemButton>
        {compartible && (
          <ListItemButton disabled={!pdf || accion !== null} onClick={compartir}>
            <ListItemIcon>{icono('compartir', <ShareOutlined />)}</ListItemIcon>
            <ListItemText primary="Compartir" secondary="WhatsApp, correo o imprimir desde el teléfono" />
          </ListItemButton>
        )}
        <ListItemButton disabled={!pdf || accion !== null} onClick={descargar}>
          <ListItemIcon>{icono('descargar', <FileDownloadOutlined />)}</ListItemIcon>
          <ListItemText primary="Descargar" />
        </ListItemButton>
      </List>

      {/* Acciones del proyecto, ej. "Enviar por WhatsApp" (Waapi) */}
      {impresion.accionesExtra && (
        <Box sx={{ px: 2, pt: 1 }}>
          {impresion.accionesExtra({
            documento: documento ?? null,
            titulo,
            filename,
            destinatario,
            whatsapp,
            obtenerUrlPublica: () => obtenerUrlPublicaPdf(pdf, obtenerPdf),
            ocupado: accion !== null,
          })}
        </Box>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2, pt: 1.5, alignItems: 'flex-start', color: 'text.secondary' }}
      >
        <PhonelinkOffOutlined fontSize="small" sx={{ mt: '2px' }} />
        <Typography variant="caption">{MENSAJE_ISI_PRINT_NO_SOPORTADO}</Typography>
      </Stack>

      <Box sx={{ px: 2, pt: 1.5 }}>
        <Button fullWidth color="inherit" onClick={onClose}>
          Cerrar
        </Button>
      </Box>
    </Drawer>
  )
}

export default ImpresionDocumentoMovil
